import {
  CatalogVariantList,
  CategoryList,
  Product,
  ProductList,
  VariantCost,
  type VariantProfile,
} from '@deizi/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from './api';

export const productsKey = ['products'] as const;

export interface ProductFilters {
  segment: 'all' | 'idea' | 'prototype' | 'active' | 'archived' | 'custom';
  category: string;
  material: string;
  tag?: string;
  q?: string;
}

export function productParams(f: ProductFilters): URLSearchParams {
  const params = new URLSearchParams({ limit: '200', segment: f.segment });
  if (f.category) params.set('category', f.category);
  if (f.material) params.set('material', f.material);
  if (f.tag) params.set('tag', f.tag);
  if (f.q?.trim()) params.set('q', f.q.trim());
  return params;
}

export function useProducts(f: ProductFilters) {
  const params = productParams(f);
  return useQuery({
    queryKey: [...productsKey, 'list', params.toString()],
    queryFn: () => api.get(`/products?${params.toString()}`, ProductList),
    placeholderData: (prev) => prev,
  });
}

export function useProduct(id: string | undefined) {
  return useQuery({
    queryKey: [...productsKey, id],
    queryFn: () => api.get(`/products/${id}`, Product),
    enabled: !!id,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: [...productsKey, 'categories'],
    queryFn: () => api.get('/products/categories', CategoryList),
  });
}

const CostPreview = z.object({ cost: VariantCost.nullable(), missing: z.string().nullable() });

/** Live unit cost of a profile being edited (stateless endpoint). */
export function useCostPreview(profile: VariantProfile | null) {
  const json = profile ? JSON.stringify(profile) : null;
  return useQuery({
    queryKey: ['variant-cost', json],
    queryFn: () =>
      api.post('/variants/cost-preview', CostPreview, { profile: JSON.parse(json ?? '{}') }),
    enabled: json !== null,
    placeholderData: (prev) => prev,
  });
}

export function useInvalidateProducts() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: productsKey });
}

/** Sellable variants with cost and suggested price on a channel (`GET /variants`). */
export function useCatalog(channelId: string | null, q: string) {
  const params = new URLSearchParams();
  if (channelId) params.set('channelId', channelId);
  if (q.trim()) params.set('q', q.trim());
  return useQuery({
    queryKey: [...productsKey, 'catalog', params.toString()],
    queryFn: () => api.get(`/variants?${params.toString()}`, CatalogVariantList),
    placeholderData: (prev) => prev,
  });
}
